<template>
    <div class="attachment-card mt-3">
        <button type="button" class="attachment-header" :aria-expanded="showFiles ? 'true' : 'false'" @click="toggleFiles">
            <div class="attachment-header-left">
                <feather type="paperclip" class="attachment-header-icon" />
                <strong>{{ $tl("labels.booking.reservations.attachments") }}</strong>
            </div>
            <div class="attachment-header-right">
                <small class="attachment-count">{{ fileCountLabel }}</small>
                <feather :type="showFiles ? 'chevron-up' : 'chevron-down'" class="attachment-header-chevron" />
            </div>
        </button>

        <div v-if="!showFiles && (!readOnly || files.length === 0)" class="attachment-collapsed">
            <span class="attachment-empty">{{ collapsedLabel }}</span>
            <button v-if="canAddFiles && !readOnly" type="button" class="attachment-link" @click.stop="openFileDialog">
                {{ $tl("labels.booking.reservations.addFile") }}
                <feather type="chevron-down" class="attachment-link-icon" />
            </button>
        </div>

        <div v-show="showFiles === true || (readOnly && files.length > 0)" class="attachment-body">
            <div
                v-if="!readOnly && canAddFiles"
                class="drop-container"
                ref="dropContainer"
                @dragover.prevent
                @dragenter="onDragEnter"
                @dragleave="onDragLeave"
                @drop="onDrop">
                <div class="drop-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" height="48" width="48">
                        <path
                            fill="#9aa0a6"
                            d="M448 464l-256 0c-8.8 0-16-7.2-16-16l0-80-48 0 0 80c0 35.3 28.7 64 64 64l256 0c35.3 0 64-28.7 64-64l0-293.5c0-17-6.7-33.3-18.7-45.3L402.7 18.7C390.7 6.7 374.5 0 357.5 0L192 0c-35.3 0-64 28.7-64 64l0 192 48 0 0-192c0-8.8 7.2-16 16-16l160 0 0 80c0 17.7 14.3 32 32 32l80 0 0 288c0 8.8-7.2 16-16 16zM297 215c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l39 39L24 288c-13.3 0-24 10.7-24 24s10.7 24 24 24l278.1 0-39 39c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l80-80c9.4-9.4 9.4-24.6 0-33.9l-80-80z" />
                    </svg>
                </div>
                <div class="drop-text">
                    <div class="drop-title">{{ $tl("labels.booking.reservations.dropFiles") }}</div>
                    <div class="drop-or">{{ $tl("labels.or") }}</div>
                    <button type="button" class="file-select-btn" @click.stop="openFileDialog">
                        <feather type="upload" class="file-select-icon" />
                        {{ $tl("labels.booking.reservations.selectFiles") }}
                    </button>
                </div>
                <input
                    type="file"
                    id="files"
                    ref="fileInput"
                    class="file-input"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.docx,.xlsx"
                    @change="handleFiles" />
            </div>

            <div class="attachment-info">{{ requirementsLabel }}</div>

            <div v-if="files.length > 0" class="attachment-list">
                <div v-for="(file, index) in files" :key="index" class="attachment-item">
                    <div class="attachment-item-left">
                        <div class="attachment-file-icon" :class="fileBadgeClass(file)">
                            <img v-if="isImage(file) && getPreviewUrl(file)" :src="getPreviewUrl(file)" :alt="file.name" />
                            <span v-else>{{ getFileExtension(file) }}</span>
                        </div>
                        <div class="attachment-file-name">{{ file.name }}</div>
                    </div>
                    <div class="attachment-item-right">
                        <span class="attachment-file-size">{{ formatSize(file.size) }}</span>
                        <a
                            v-if="getFileActionUrl(file)"
                            :href="getFileActionUrl(file)"
                            :target="isDownloadAction(file) ? null : '_blank'"
                            :rel="isDownloadAction(file) ? null : 'noopener'"
                            :download="isDownloadAction(file) ? file.name : null"
                            class="attachment-link">
                            {{ getFileActionLabel(file) }}
                        </a>
                        <button v-if="!readOnly" type="button" class="attachment-link" @click="removeFile(index)">
                            {{ $tl("labels.form.file.delete") }}
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="displayErrors.length > 0" class="attachment-error">
                <feather type="alert-circle" class="attachment-error-icon" />
                <div class="attachment-error-messages">
                    <div v-for="(error, index) in displayErrors" :key="index">{{ error }}</div>
                </div>
            </div>

            <div v-if="files.length > 0" class="attachment-info attachment-info-bottom">{{ requirementsLabel }}</div>
        </div>
    </div>
</template>

<script>
export default {
    name: "FileUploader",
    props: {
        value: Array,
        readOnly: {
            type: Boolean,
            default: false,
        },
        errors: {
            type: Array,
            default: () => [],
        },
        maxFiles: {
            type: Number,
            default: 5,
        },
        maxFileSizeMb: {
            type: Number,
            default: 5,
        },
    },
    data() {
        return {
            files: [],
            showFiles: false,
            previewUrls: {},
            localErrors: [],
        };
    },
    computed: {
        canAddFiles() {
            if (!this.maxFiles) {
                return true;
            }
            return this.files.length < this.maxFiles;
        },
        fileCountLabel() {
            if (!this.maxFiles) {
                return `${this.files.length}`;
            }
            return `${this.files.length}/${this.maxFiles}`;
        },
        collapsedLabel() {
            if (this.files.length === 0) {
                return this.$tl("labels.booking.reservations.noFileAdded");
            }
            return this.$tcl("labels.booking.reservations.files", this.files.length);
        },
        acceptedFormatsLabel() {
            return "JPG, PNG, WEBP, PDF, DOCX, XLSX";
        },
        maxFileSizeLabel() {
            return `${this.maxFileSizeMb} MB`;
        },
        requirementsLabel() {
            const formats = this.$tl("labels.booking.reservations.attachmentsFormats", undefined, {
                formats: this.acceptedFormatsLabel,
            });
            const maxSize = this.$tl("labels.booking.reservations.attachmentsMaxSize", undefined, {
                size: this.maxFileSizeLabel,
            });
            const count = this.maxFiles || 0;
            const maxFiles = this.$tl("labels.booking.reservations.attachmentsMaxFiles", undefined, {
                count,
            });
            return `${formats} · ${maxSize} · ${maxFiles}`;
        },
        displayErrors() {
            const externalErrors = Array.isArray(this.errors) ? this.errors : [];
            return [...this.localErrors, ...externalErrors].filter((value) => typeof value === "string" && value !== "");
        },
    },
    watch: {
        value: {
            immediate: true,
            deep: true,
            handler(v) {
                const nextFiles = Array.isArray(v) ? v : [];
                if (this.areFilesEqual(nextFiles, this.files)) {
                    return;
                }
                this.files = nextFiles;
                if (this.files.length > 0 && !this.readOnly) {
                    this.showFiles = true;
                }
            },
        },
        files(newFiles) {
            this.$emit("input", newFiles);
            if (newFiles.length > 0 && !this.readOnly) {
                this.showFiles = true;
            }
            this.cleanupObjectUrls();
            this.syncInputFiles();
        },
        readOnly: {
            immediate: true,
            handler(isReadOnly) {
                if (isReadOnly && this.files.length > 0) {
                    this.showFiles = true;
                }
            },
        },
        errors: {
            immediate: true,
            handler(newErrors) {
                if (Array.isArray(newErrors) && newErrors.length > 0) {
                    this.showFiles = true;
                }
            },
        },
    },
    methods: {
        toggleFiles() {
            if (this.readOnly && this.files.length === 0) {
                return;
            }
            if (this.readOnly && this.files.length > 0) {
                this.showFiles = true;
                return;
            }
            this.showFiles = !this.showFiles;
        },
        openFileDialog() {
            if (this.readOnly || !this.canAddFiles) {
                return;
            }
            if (!this.showFiles) {
                this.showFiles = true;
                this.$nextTick(() => {
                    if (this.$refs.fileInput) {
                        this.$refs.fileInput.click();
                    }
                });
                return;
            }
            if (this.$refs.fileInput) {
                this.$refs.fileInput.click();
            }
        },
        handleFiles(event) {
            this.addFiles(event.target.files);
        },
        onDrop(event) {
            event.preventDefault();
            this.addFiles(event.dataTransfer.files);
        },
        onDragEnter() {},
        onDragLeave() {},
        addFiles(fileList) {
            if (!fileList || fileList.length === 0) {
                return;
            }

            this.localErrors = [];
            const available = this.maxFiles ? Math.max(this.maxFiles - this.files.length, 0) : fileList.length;
            if (available === 0) {
                this.localErrors.push(
                    this.$tl("errors.common.file.tooMany", undefined, {
                        max: this.maxFiles,
                    })
                );
                return;
            }
            if (this.maxFiles && fileList.length > available) {
                this.localErrors.push(
                    this.$tl("errors.common.file.tooMany", undefined, {
                        max: this.maxFiles,
                    })
                );
            }

            let added = 0;
            for (let i = 0; i < fileList.length && added < available; i++) {
                if (!this.isAllowedFile(fileList[i])) {
                    this.localErrors.push(
                        this.$tl("errors.common.file.invalidFormat", undefined, {
                            formats: this.acceptedFormatsLabel,
                        })
                    );
                    continue;
                }
                if (!this.isAllowedSize(fileList[i])) {
                    this.localErrors.push(
                        this.$tl("errors.common.file.tooBig", undefined, {
                            fileSize: this.formatMb(fileList[i].size),
                            maxSize: this.maxFileSizeMb,
                        })
                    );
                    continue;
                }
                this.files.push(fileList[i]);
                added += 1;
            }
            if (this.localErrors.length > 0) {
                this.showFiles = true;
            }
            this.syncInputFiles();
        },
        removeFile(index) {
            this.files.splice(index, 1);
            this.localErrors = [];
            this.syncInputFiles();
        },
        syncInputFiles() {
            if (!this.$refs.fileInput) {
                return;
            }
            const dataTransfer = new DataTransfer();
            this.files.filter(this.isFileObject).forEach((file) => dataTransfer.items.add(file));
            this.$refs.fileInput.files = dataTransfer.files;
        },
        formatSize(bytes) {
            if (!bytes) {
                return "0 Ko";
            }
            return `${Math.round(bytes / 1024)} Ko`;
        },
        isFileObject(file) {
            return typeof File !== "undefined" && file instanceof File;
        },
        isImage(file) {
            const type = (file.type || "").toLowerCase();
            if (type.startsWith("image/")) {
                return true;
            }
            return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file.name || "");
        },
        isAllowedFile(file) {
            const type = (file.type || "").toLowerCase();
            if (
                type === "application/pdf" ||
                type === "image/jpeg" ||
                type === "image/png" ||
                type === "image/webp" ||
                type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            ) {
                return true;
            }
            return /\.(pdf|jpe?g|png|webp|docx|xlsx)$/i.test(file.name || "");
        },
        isAllowedSize(file) {
            if (!this.maxFileSizeMb || !file || typeof file.size !== "number") {
                return true;
            }
            return file.size <= this.maxFileSizeMb * 1024 * 1024;
        },
        formatMb(bytes) {
            if (!bytes) {
                return 0;
            }
            return Math.round((bytes / 1024 / 1024) * 10) / 10;
        },
        isPdf(file) {
            const type = (file.type || "").toLowerCase();
            if (type === "application/pdf") {
                return true;
            }
            return /\.pdf$/i.test(file.name || "");
        },
        getFileExtension(file) {
            const name = file.name || "";
            const parts = name.split(".");
            if (parts.length < 2) {
                return "FILE";
            }
            return parts[parts.length - 1].toUpperCase();
        },
        fileBadgeClass(file) {
            if (this.isPdf(file)) {
                return "file-badge--pdf";
            }
            if (this.isImage(file)) {
                return "file-badge--image";
            }
            return "file-badge--default";
        },
        areFilesEqual(nextFiles, currentFiles) {
            if (nextFiles === currentFiles) {
                return true;
            }
            if (!Array.isArray(nextFiles) || !Array.isArray(currentFiles)) {
                return false;
            }
            if (nextFiles.length !== currentFiles.length) {
                return false;
            }
            for (let i = 0; i < nextFiles.length; i++) {
                if (this.getFileIdentity(nextFiles[i]) !== this.getFileIdentity(currentFiles[i])) {
                    return false;
                }
            }
            return true;
        },
        getFileIdentity(file) {
            if (this.isFileObject(file)) {
                const lastModified = file.lastModified || 0;
                return `file:${file.name}-${file.size}-${lastModified}`;
            }
            if (file && typeof file === "object") {
                const path = file.path || "";
                const name = file.name || "";
                const size = typeof file.size === "number" ? file.size : "";
                return `meta:${path}-${name}-${size}`;
            }
            return String(file);
        },
        getFileKey(file) {
            if (!this.isFileObject(file)) {
                return null;
            }
            const lastModified = file.lastModified || 0;
            return `${file.name}-${file.size}-${lastModified}`;
        },
        getObjectUrl(file) {
            if (typeof URL === "undefined") {
                return null;
            }
            const key = this.getFileKey(file);
            if (!key) {
                return null;
            }
            if (!this.previewUrls[key]) {
                this.previewUrls[key] = URL.createObjectURL(file);
            }
            return this.previewUrls[key];
        },
        cleanupObjectUrls() {
            if (typeof URL === "undefined") {
                return;
            }
            const activeKeys = new Set(
                this.files
                    .filter(this.isFileObject)
                    .map((file) => this.getFileKey(file))
                    .filter((key) => key !== null)
            );
            Object.keys(this.previewUrls).forEach((key) => {
                if (!activeKeys.has(key)) {
                    URL.revokeObjectURL(this.previewUrls[key]);
                    delete this.previewUrls[key];
                }
            });
        },
        getPreviewUrl(file) {
            if (file.url) {
                return file.url;
            }
            if (this.isFileObject(file)) {
                return this.getObjectUrl(file);
            }
            return null;
        },
        getFileViewUrl(file) {
            return this.getPreviewUrl(file);
        },
        getFileDownloadUrl(file) {
            if (file.downloadUrl) {
                return file.downloadUrl;
            }
            return this.getFileViewUrl(file);
        },
        getFileActionType(file) {
            if (this.isImage(file)) {
                return "image";
            }
            if (this.isPdf(file)) {
                return "pdf";
            }
            return "download";
        },
        getFileActionLabel(file) {
            const action = this.getFileActionType(file);
            if (action === "image") {
                return this.$tl("labels.form.file.viewImage");
            }
            if (action === "pdf") {
                return this.$tl("labels.form.file.viewPdf");
            }
            return this.$tl("labels.form.file.download");
        },
        getFileActionUrl(file) {
            const action = this.getFileActionType(file);
            if (action === "download") {
                return this.getFileDownloadUrl(file);
            }
            return this.getFileViewUrl(file);
        },
        isDownloadAction(file) {
            return this.getFileActionType(file) === "download";
        },
    },
    beforeDestroy() {
        this.cleanupObjectUrls();
    },
};
</script>

<style scoped>
.attachment-card {
    background: #ffffff;
    border: 1px solid #e6e6e6;
    border-radius: 12px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    overflow: hidden;
}

.attachment-header {
    width: 100%;
    border: 0;
    background: inherit;
    text-align: left;
    font: inherit;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 24px;
    cursor: pointer;
}

.attachment-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #2d3748;
}

.attachment-header-icon {
    color: #9aa0a6;
}

.attachment-header-right {
    display: flex;
    align-items: center;
    gap: 12px;
    color: #9aa0a6;
}

.attachment-count {
    font-size: 13px;
}

.attachment-collapsed {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 24px 18px;
    border-top: 1px solid #ececec;
}

.attachment-empty {
    color: #8c8f94;
}

.attachment-body {
    padding: 24px;
    border-top: 1px solid #ececec;
}

.drop-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    min-height: 160px;
    padding: 24px;
    border: 2px dashed #d9d9d9;
    border-radius: 12px;
    background: #fafafa;
    text-align: center;
}

.drop-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.drop-title {
    color: #6b7280;
    font-size: 16px;
    font-weight: 600;
}

.drop-or {
    color: #9aa0a6;
    font-size: 14px;
}

.file-select-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: none;
    border-radius: 8px;
    background: var(--main-color-light, #30a4b7);
    color: #ffffff;
    padding: 8px 16px;
    font-size: 14px;
    cursor: pointer;
}

.file-select-btn:hover {
    background: var(--main-color-light-hover, #2a8f9f);
}

.file-select-icon {
    width: 16px;
    height: 16px;
}

.file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
}

.attachment-info {
    margin-top: 12px;
    font-size: 13px;
    color: #8c8f94;
}

.attachment-info-bottom {
    margin-top: 18px;
}

.attachment-list {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.attachment-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid #e6e6e6;
    background: #ffffff;
}

.attachment-item-left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.attachment-file-icon {
    width: 36px;
    height: 36px;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    background: #f5f5f5;
    color: #4b5563;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.attachment-file-icon img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.file-badge--pdf {
    color: #d64545;
    border-color: #f1c6c6;
    background: #fff5f5;
}

.file-badge--image {
    color: #2f80ed;
    border-color: #cfe1ff;
    background: #eef4ff;
}

.file-badge--default {
    color: #6b7280;
    border-color: #e5e7eb;
    background: #f3f4f6;
}

.attachment-file-name {
    font-weight: 500;
    color: #2d3748;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 320px;
}

.attachment-item-right {
    display: flex;
    align-items: center;
    gap: 16px;
    white-space: nowrap;
}

.attachment-file-size {
    font-size: 13px;
    color: #6b7280;
    min-width: 60px;
    text-align: right;
}

.attachment-link {
    display: inline-flex;
    align-items: center;
    background: none;
    border: none;
    padding: 0;
    color: var(--main-color-light, #30a4b7);
    font-size: 13px;
    cursor: pointer;
}

.attachment-link:hover {
    color: var(--main-color-light-hover, #2a8f9f);
    text-decoration: underline;
}

.attachment-link-icon {
    width: 16px;
    height: 16px;
    margin-left: 6px;
}

.attachment-error {
    margin-top: 12px;
    padding: 12px 14px;
    border-radius: 8px;
    border: 1px solid #f1c6c6;
    background: #fff5f5;
    color: #c53030;
    display: flex;
    gap: 10px;
    align-items: flex-start;
}

.attachment-error-icon {
    width: 18px;
    height: 18px;
    margin-top: 2px;
    color: #e53e3e;
}

.attachment-error-messages {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13px;
}

@media (max-width: 768px) {
    .attachment-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .attachment-item-right {
        width: 100%;
        justify-content: space-between;
    }

    .attachment-file-name {
        max-width: 100%;
    }
}
</style>
